import React, { useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames/bind';
import styles from './styles.styl';
import { TextField } from '@consta/uikit/TextField';
import { IconSearch } from '@consta/uikit/IconSearch';
import { Button } from '@consta/uikit/Button';
import { Text } from '@consta/uikit/Text';
import { IconSortDown } from '@consta/uikit/IconSortDown';
import { IconUnsort } from '@consta/uikit/IconUnsort';
import { IconSortUp } from '@consta/uikit/IconSortUp';
import { IconAdd } from '@consta/uikit/IconAdd';
import NumberLines from 'src/components/NumberLines';
import {
  debounceTimeModel,
  filterSearchLetters,
  NumberLinesItems,
} from 'src/utils/config';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  FilterPageEnum,
  InitTypeModalEnum,
  RoleEnum,
  SortDatePageEnum,
  SortModalEnum,
} from 'src/utils/enum';
import CardModal from 'src/components/CardModal';
import { useMediaQuery } from 'react-responsive';
import Masonry from 'react-masonry-css';
import { Modal } from '@consta/uikit/Modal';
import { Sidebar } from '@consta/uikit/Sidebar';
import AddModal from 'src/components/AddModal';
import { useDispatch } from 'react-redux';
import { loadAllModels } from 'src/redux/features/methodModels/allModels/actions';
import { useShallowEqualSelector } from 'src/customHooks/useShallowEqualSelector';
import {
  allModelsLoadedSelector,
  allModelsPendingSelector,
  allModelsSelector,
} from 'src/redux/features/methodModels/allModels/selectors';
import { State } from 'src/redux/reducer';
import CenterLoader from 'src/components/CenterLoader';
import { useDebouncedCallback } from 'use-debounce';
import { storageRole } from 'src/utils/storage';
import RunningModal from 'src/components/RunningModal';
import { CardModalPropType } from 'src/utils/type';
import { loadRunModels } from 'src/redux/features/methodModels/runModels/actions';
import ViewModal from 'src/components/ViewModal';

const cx = cn.bind(styles);

export interface AllModelProps {}

const AllModel: React.FC<AllModelProps> = () => {
  const breakpoints = {
    default: 3,
    1920: 3,
    1540: 2,
    1280: 2,
    1024: 1,
    360: 1,
  };

  const isMobile = useMediaQuery({ maxWidth: 460 });

  const dispatch = useDispatch();

  const location = useLocation();

  const vh = window.innerHeight * 0.01;

  document.documentElement.style.setProperty('--vh', `${vh}px`);

  const [allModels, isAllModelsLoaded, isAllModelsPending] =
    useShallowEqualSelector((state: State) => [
      allModelsSelector(state),
      allModelsLoadedSelector(state),
      allModelsPendingSelector(state),
    ]);

  const [searchValue, setSearchValue] = useState('');
  const [activeNumber, setActiveNumber] = useState(NumberLinesItems[0]);
  const [currentPage, setCurrentPage] = useState(1);

  const [isAddModal, setIsAddModal] = useState(false);

  const [isViewModal, setIsViewModal] = useState(false);
  const [isRunningModal, setIsRunningModal] = useState(false);
  const [isEditCardModal, setIsEditCardModal] = useState(false);

  const [indoModal, setInfoModal] = useState(null);

  const [dateChange, setDateChange] = useState({
    id: SortModalEnum.ICON_UN,
    comp: IconUnsort,
  });

  const [dateLast, setDateLast] = useState({
    id: SortModalEnum.ICON_UN,
    comp: IconUnsort,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);

  useEffect(() => {
    if (Object.keys(params).length) {
      setCurrentPage(params?.page ? Number(params?.page) : 1);
      setActiveNumber(
        params?.per_page
          ? { id: Number(params?.per_page), label: Number(params?.per_page) }
          : NumberLinesItems[0],
      );
      setSearchValue(params?.name ? params.name : '');
      if (params?.sort === SortDatePageEnum.UPDATE_AT) {
        setDateChange(
          params?.desc === 'false'
            ? {
                id: SortModalEnum.ICON_UP,
                comp: IconSortUp,
              }
            : {
                id: SortModalEnum.ICON_DOWN,
                comp: IconSortDown,
              },
        );
      }
      if (params?.sort === SortDatePageEnum.LAST_RUN_AT) {
        setDateLast(
          params?.desc === 'false'
            ? {
                id: SortModalEnum.ICON_UP,
                comp: IconSortUp,
              }
            : {
                id: SortModalEnum.ICON_DOWN,
                comp: IconSortDown,
              },
        );
      }
    } else {
      setSearchParams(
        {
          [FilterPageEnum.PER_PAGE]: String(activeNumber.label),
          [FilterPageEnum.PAGE]: String(currentPage),
        },
        {
          replace: true,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    location.search && dispatch(loadAllModels(`${location.search}&all=true`));
  }, [dispatch, location.search]);

  const handleClickDateChange = useCallback(() => {
    setCurrentPage(1);
    searchParams.set(FilterPageEnum.PAGE, '1');
    setDateLast({
      id: SortModalEnum.ICON_UN,
      comp: IconUnsort,
    });
    if (dateChange.id === SortModalEnum.ICON_UN) {
      setDateChange({
        id: SortModalEnum.ICON_UP,
        comp: IconSortUp,
      });
      searchParams.set(FilterPageEnum.SORT, SortDatePageEnum.UPDATE_AT);
      searchParams.set(FilterPageEnum.DESC, 'false');
      setSearchParams(searchParams);
    } else if (dateChange.id === SortModalEnum.ICON_UP) {
      setDateChange({
        id: SortModalEnum.ICON_DOWN,
        comp: IconSortDown,
      });
      searchParams.set(FilterPageEnum.SORT, SortDatePageEnum.UPDATE_AT);
      searchParams.set(FilterPageEnum.DESC, 'true');
      setSearchParams(searchParams);
    } else if (dateChange.id === SortModalEnum.ICON_DOWN) {
      setDateChange({
        id: SortModalEnum.ICON_UN,
        comp: IconUnsort,
      });
      searchParams.delete(FilterPageEnum.SORT);
      searchParams.delete(FilterPageEnum.DESC);
      setSearchParams(searchParams);
    }
  }, [dateChange, searchParams, setSearchParams]);

  const handleClickDateLast = useCallback(() => {
    setCurrentPage(1);
    searchParams.set(FilterPageEnum.PAGE, '1');
    setDateChange({
      id: SortModalEnum.ICON_UN,
      comp: IconUnsort,
    });
    if (dateLast.id === SortModalEnum.ICON_UN) {
      setDateLast({
        id: SortModalEnum.ICON_UP,
        comp: IconSortUp,
      });
      searchParams.set(FilterPageEnum.SORT, SortDatePageEnum.LAST_RUN_AT);
      searchParams.set(FilterPageEnum.DESC, 'false');
      setSearchParams(searchParams);
    } else if (dateLast.id === SortModalEnum.ICON_UP) {
      setDateLast({
        id: SortModalEnum.ICON_DOWN,
        comp: IconSortDown,
      });
      searchParams.set(FilterPageEnum.SORT, SortDatePageEnum.LAST_RUN_AT);
      searchParams.set(FilterPageEnum.DESC, 'true');
      setSearchParams(searchParams);
    } else if (dateLast.id === SortModalEnum.ICON_DOWN) {
      setDateLast({
        id: SortModalEnum.ICON_UN,
        comp: IconUnsort,
      });
      searchParams.delete(FilterPageEnum.SORT);
      searchParams.delete(FilterPageEnum.DESC);
      setSearchParams(searchParams);
    }
  }, [dateLast, searchParams, setSearchParams]);

  const handleSearch = useDebouncedCallback((value: string) => {
    setCurrentPage(1);
    searchParams.set(FilterPageEnum.PAGE, '1');
    if (!value) {
      searchParams.delete(FilterPageEnum.NAME);
      setSearchParams(searchParams);
    } else if (value?.length > filterSearchLetters) {
      searchParams.set(FilterPageEnum.NAME, value);
      setSearchParams(searchParams);
    }
  }, debounceTimeModel);

  const viewModel = useMemo(() => {
    return (
      <ViewModal
        setIsRunningModal={setIsRunningModal}
        setIsViewModal={setIsViewModal}
        id={indoModal?.id}
        title={indoModal?.title}
        text={indoModal?.text}
        version={indoModal?.version}
        type={indoModal?.type}
        url={indoModal?.url}
        image={indoModal?.image}
        manual={indoModal?.manual}
      />
    );
  }, [indoModal]);

  const handleClickModal = useCallback((info: CardModalPropType) => {
    setInfoModal(info);
    if (storageRole() === RoleEnum.USER) {
      setIsViewModal(true);
    } else {
      setIsEditCardModal(true);
    }
  }, []);

  const handleClickRunning = useCallback(
    (info: CardModalPropType) => {
      if (info.type !== InitTypeModalEnum.ANY_LOGIC) {
        window.open(info.url);
        dispatch(loadRunModels(info.id));
      } else {
        setInfoModal(info);
        setIsRunningModal(true);
      }
    },
    [dispatch],
  );

  return (
    <div className={cx('AllModel')}>
      <div className={cx('AllModel__header')}>
        <div className={cx('AllModel__searchContainer')}>
          <TextField
            id="search"
            onChange={({ value }) => {
              setSearchValue(value), handleSearch(value);
            }}
            value={searchValue}
            leftSide={IconSearch}
            className={cx('AllModel__fieldSearch')}
            size="s"
            placeholder={'Введите название модели'}
            width={isMobile ? 'full' : 'default'}
          />
          <div className={cx('AllModel__search')}>
            <div className={cx('AllModel__sort')}>
              <Button
                onClick={() => handleClickDateChange()}
                onlyIcon
                iconLeft={dateChange.comp}
                size="s"
                iconSize="s"
                view="clear"
                disabled={isAllModelsPending}
                className={cx('AllModel__button', {
                  AllModel__button_active:
                    dateChange.id !== SortModalEnum.ICON_UN,
                })}
              />
              <Text size="s">По дате изменения</Text>
            </div>
            <div className={cx('AllModel__sort')}>
              <Button
                onClick={() => handleClickDateLast()}
                onlyIcon
                iconLeft={dateLast.comp}
                size="s"
                iconSize="s"
                view="clear"
                disabled={isAllModelsPending}
                className={cx('AllModel__button', {
                  AllModel__button_active:
                    dateLast.id !== SortModalEnum.ICON_UN,
                })}
              />
              <Text size="s">По дате последнего запуска</Text>
            </div>
          </div>
        </div>
        {storageRole() !== RoleEnum.USER && (
          <Button
            onClick={() => setIsAddModal(true)}
            label={'Добавить модель'}
            size="s"
            iconLeft={IconAdd}
            iconSize="s"
            width={isMobile ? 'full' : 'default'}
          />
        )}
      </div>
      {isAllModelsLoaded ? (
        <React.Fragment>
          <div className={cx('AllModel__content')}>
            <Masonry
              breakpointCols={breakpoints}
              className={cx('AllModel__grid')}
              columnClassName={cx('AllModel__gridColumn')}
            >
              {allModels?.items.map((item) => (
                <div key={item.id}>
                  <CardModal
                    id={item.id}
                    title={item.name}
                    version={item.versions}
                    image={item.files}
                    text={item.description}
                    manual={item.manual}
                    dateChange={item.updated_at}
                    dateLast={item.last_run_at}
                    type={item.model_type}
                    url={item.service_url}
                    isMyModal={false}
                    handleClickModal={handleClickModal}
                    handleClickRunning={handleClickRunning}
                    className={cx('AllModel__gridItem')}
                  />
                </div>
              ))}
            </Masonry>
          </div>

          <div className={cx('AllModel__pag')}>
            <NumberLines
              valuePage={activeNumber}
              handleChange={(value) => {
                searchParams.set(FilterPageEnum.PER_PAGE, String(value.label));
                setSearchParams(searchParams);
                setActiveNumber(value);
              }}
              currentPage={currentPage - 1}
              totalPages={allModels?.pages}
              onChangePagination={(value) => {
                searchParams.set(FilterPageEnum.PAGE, String(value + 1));
                setSearchParams(searchParams);
                setCurrentPage(value + 1);
              }}
            />
          </div>
        </React.Fragment>
      ) : (
        <CenterLoader />
      )}
      {isMobile ? (
        <Sidebar
          className={cx('AllModel__sideBar')}
          position="left"
          isOpen={isAddModal}
          size="full"
        >
          <Sidebar.Content className={cx('AllModel__sideBarContent')}>
            <AddModal
              isMyModal={false}
              isAddModal={true}
              setIsAddModal={setIsAddModal}
            />
          </Sidebar.Content>
        </Sidebar>
      ) : (
        <Modal
          position="center"
          isOpen={isAddModal}
          hasOverlay
          className={cx('AllModel__modalView')}
        >
          <AddModal
            isMyModal={false}
            isAddModal={true}
            setIsAddModal={setIsAddModal}
          />
        </Modal>
      )}
      {isMobile ? (
        <Sidebar
          className={cx('AllModel__sideBar')}
          position="left"
          isOpen={isViewModal}
          size="full"
        >
          <Sidebar.Content className={cx('AllModel__sideBarContent')}>
            {viewModel}
          </Sidebar.Content>
        </Sidebar>
      ) : (
        <Modal
          position="top"
          isOpen={isViewModal}
          hasOverlay
          className={cx('AllModel__modalView')}
        >
          {viewModel}
        </Modal>
      )}
      {isMobile ? (
        <Sidebar
          className={cx('AllModel__sideBar')}
          position="left"
          isOpen={isRunningModal}
          size="full"
        >
          <Sidebar.Content className={cx('AllModel__sideBarContent')}>
            <RunningModal
              nameModal={indoModal?.title}
              setIsRunningModal={setIsRunningModal}
              manual={indoModal?.manual}
              modelId={indoModal?.id}
            />
          </Sidebar.Content>
        </Sidebar>
      ) : (
        <Modal
          position="center"
          isOpen={isRunningModal}
          hasOverlay
          className={cx('AllModel__modalView')}
        >
          <RunningModal
            nameModal={indoModal?.title}
            setIsRunningModal={setIsRunningModal}
            manual={indoModal?.manual}
            modelId={indoModal?.id}
          />
        </Modal>
      )}
      {isMobile ? (
        <Sidebar
          className={cx('AllModel__sideBar')}
          position="left"
          isOpen={isEditCardModal}
          size="full"
        >
          <Sidebar.Content className={cx('AllModel__sideBarContent')}>
            <AddModal
              modelId={indoModal?.id}
              isAddModal={false}
              isMyModal={false}
              setIsAddModal={setIsEditCardModal}
            />
          </Sidebar.Content>
        </Sidebar>
      ) : (
        <Modal
          position="center"
          isOpen={isEditCardModal}
          hasOverlay
          className={cx('AllModel__modalView')}
        >
          <AddModal
            modelId={indoModal?.id}
            isAddModal={false}
            isMyModal={false}
            setIsAddModal={setIsEditCardModal}
          />
        </Modal>
      )}
    </div>
  );
};
export default AllModel;
